import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from './Toast.module.css';

const Toast = ({ id, title, description, color, duration, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [remaining, setRemaining] = useState(duration);
  const [isMounted, setIsMounted] = useState(false);
  const startTimeRef = useRef(Date.now());
  const timeoutRef = useRef(null);

  const handleRemove = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onRemove(id);
    }, 300); // Match animation duration
  }, [id, onRemove]);

  const pause = () => {
    setIsPaused(true);
    const elapsed = Date.now() - startTimeRef.current;
    setRemaining((prev) => Math.max(0, prev - elapsed));
    clearTimeout(timeoutRef.current);
  };

  const resume = () => {
    setIsPaused(false);
    startTimeRef.current = Date.now();
  };

  useEffect(() => {
    setIsVisible(true);
    
    // Use a small timeout to ensure the initial state (scaleX(1)) is rendered
    // before we start the transition to scaleX(0).
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 10);
    
    const handleBlur = () => pause();
    const handleFocus = () => resume();

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    if (duration <= 0) return;

    if (!isPaused) {
      timeoutRef.current = setTimeout(handleRemove, remaining);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [isPaused, remaining, handleRemove, duration]);

  const progressScale = duration > 0 ? (isPaused ? remaining / duration : (isMounted ? 0 : 1)) : 1;
  const progressTransition = isPaused || !isMounted ? 'none' : `transform ${remaining}ms linear`;

  return (
    <div
      className={`${styles.toast} ${styles[color]} ${isVisible ? styles.visible : ''}`}
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      <div className={styles.body}>
        {title && <div className={styles.title}>{title}</div>}
        {description && <div className={styles.description}>{description}</div>}
        <button
          type="button"
          className={styles.close}
          onClick={handleRemove}
          aria-label="Close"
        >
          &times;
        </button>
      </div>
      {duration > 0 && (
        <div className={styles.progressTrack}>
          <div
            className={styles.progressBar}
            style={{
              transform: `scaleX(${progressScale})`,
              transition: progressTransition,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
